import React, { useState } from 'react';
import {
  Modal,
  Card,
  CardContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function TopGrantsModal({ selectedGrant, relatedFaculty, onClose, onExternalLinkClick }) {
  const theme = useTheme();
  const [visibleFacultyCount, setVisibleFacultyCount] = useState(5);

  const loadMoreFaculty = () => {
    setVisibleFacultyCount((prev) => prev + 5);
  };

  if (!selectedGrant) return null;

  return (
    <Modal
      open={!!selectedGrant}
      onClose={onClose}
      aria-labelledby="grant-details-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
      }}
    >
      <Card
        sx={{
          width: '80%',
          maxHeight: '80vh',
          position: 'relative',
          borderRadius: theme.spacing(1),
          boxShadow: theme.shadows[5],
        }}
      >
        {/* Fixed Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
            zIndex: 20,
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>

        {/* Sticky Header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 10,
            padding: theme.spacing(2),
            paddingRight: theme.spacing(6), // Added extra padding on the right to avoid overlap
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" component="h2">
            {selectedGrant.name}
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ overflowY: 'auto', maxHeight: 'calc(80vh - 70px)' }}>
          <CardContent sx={{ paddingBottom: theme.spacing(8) }}>
            {/* Agency Section */}
            <Box sx={{ marginBottom: theme.spacing(3), paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                Agency
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(1) }} />
              <Box
                sx={{
                  padding: theme.spacing(2),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Typography variant="body1">{selectedGrant.agency_name}</Typography>
              </Box>
            </Box>

            {/* Estimated Funding Section */}
            <Box sx={{ marginBottom: theme.spacing(3), paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                Estimated Funding
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(1) }} />
              <Box
                sx={{
                  padding: theme.spacing(2),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Typography variant="body1">{selectedGrant.estimated_funding}</Typography>
              </Box>
            </Box>

            {/* Synopsis Section */}
            <Box sx={{ marginBottom: theme.spacing(3), paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                Synopsis
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(1) }} />
              <Box
                sx={{
                  padding: theme.spacing(2),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {selectedGrant.synopsis}
                </Typography>
              </Box>
            </Box>

            {/* Limited Competition Section */}
            <Box sx={{ marginBottom: theme.spacing(3), paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                Limited Competition
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(1) }} />
              <Box
                sx={{
                  padding: theme.spacing(2),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                <Typography variant="body1">
                  Data Analysis and Sharing Center (DASC) for the MACS/WIHS Combined Cohort Study (MWCCS) (U01 Clinical Trials Not Allowed)
                </Typography>
              </Box>
            </Box>

            {/* External Link Section */}
            <Box sx={{ marginBottom: theme.spacing(3), paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                External Link
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(1) }} />
              <Box
                sx={{
                  padding: theme.spacing(2),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.palette.grey[50],
                }}
              >
                {selectedGrant.external_url ? (
                  <Link 
                    component="button"
                    variant="body1"
                    onClick={(e) => {
                      // Prevent the click from bubbling up to parent elements
                      e.stopPropagation();
                      // Call the onExternalLinkClick handler if it exists
                      if (onExternalLinkClick) {
                        onExternalLinkClick(selectedGrant.id, selectedGrant.external_url);
                      }
                    }}
                    sx={{ 
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                      '&:hover': { textDecoration: 'none' },
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer'
                    }}
                  >
                    View Official Grant Listing
                  </Link>
                ) : (
                  <Typography variant="body1">No external link available</Typography>
                )}
              </Box>
            </Box>

            {/* Related Faculty Section */}
            <Box sx={{ paddingX: theme.spacing(2) }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: theme.spacing(1) }}>
                Related Faculty
              </Typography>
              <Divider sx={{ marginBottom: theme.spacing(2) }} />
              {relatedFaculty.slice(0, visibleFacultyCount).map((facultyMember) => (
                <Box
                  key={facultyMember.id}
                  sx={{
                    marginBottom: theme.spacing(2),
                    padding: theme.spacing(1),
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: theme.spacing(0.5),
                  }}
                >
                  <Typography variant="body1">
                    <strong>Name:</strong> {facultyMember.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Interests:</strong>{' '}
                    {Array.isArray(facultyMember.interests)
                      ? facultyMember.interests.join(', ')
                      : facultyMember.interests}
                  </Typography>
                </Box>
              ))}
              {visibleFacultyCount < relatedFaculty.length && (
                <Box sx={{ textAlign: 'center', marginTop: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                  <Button onClick={loadMoreFaculty} variant="outlined" size="small">
                    Load More
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Box>
      </Card>
    </Modal>
  );
}

export default TopGrantsModal;
